import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { LockOrderContextProvider } from './context/LockOrderContext';
import { LockOrderContext } from './context/LockOrderContext';
import {
  SettingsContextProvider,
  SettingsContext,
} from './context/SettingsContext';
import { AuthContextProvider } from './context/AuthContext';
import { AuthContext } from './context/AuthContext';
import { useState, useEffect, useContext, Suspense, lazy } from 'react';
import { Security, LoginCallback } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import Authorize from './components/Authorize';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import HelpButton from './components/buttons/HelpButton';
import URLSearchParamsLowerCaseKey from './models/URLSearchParamsLowerCaseKey';
import { LookupContextProvider } from './context/LookupContext';
import { IdentityContextProvider } from './context/IdentityContextProvider';
import { ThemeProvider } from '@emotion/react';
import { createTheme, responsiveFontSizes } from '@mui/material';
import { ResponsiveContextProvider } from './context/ResponsiveContext';
import { useLogger } from './hooks/useLogger';
import Spinner from './components/Spinner';
import ErrorBoundary from './ErrorBoundary';
import { toast } from 'react-toastify';
import NavigationWarningAlert from './components/alerts/NavigationWarningAlert';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import BoundedElement from './components/BoundedElement';
import Order from './models/Order';

const HomePage = lazy(() => import('./pages/HomePage'));
const KeySystemPage = lazy(() => import('./pages/KeySystemPage'));
const CustomerSearchPage = lazy(() => import('./pages/CustomerSearchPage'));
const AddCustomerPage = lazy(() => import('./pages/AddCustomerPage'));
const SystemPreProcessPage = lazy(() => import('./pages/SystemPreProcessPage'));
const SystemStartPage = lazy(() => import('./pages/SystemStartPage'));
const SetPage = lazy(() => import('./pages/SetPage'));
const ChartingFileSummaryPage = lazy(
  () => import('./pages/ChartingFileSummaryPage')
);
//const SubmitSystemPage = lazy(() => import('./pages/SubmitSystemPage'))
const ErrorPage = lazy(() => import('./pages/ErrorPage'));
const EditEngravingPage = lazy(() => import('./pages/EditEngravingPage'));
const ReorderLocksPage = lazy(() => import('./pages/ReorderLocksPage'));
const SubmitReviewPage = lazy(() => import('./pages/SubmitSystemReviewPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));

const oktaAuth = new OktaAuth({
  issuer: globalThis.REACT_APP_OKTA_ISSUER,
  clientId: globalThis.REACT_APP_OKTA_CLIENT_ID,
  redirectUri:
    globalThis.location.origin + globalThis.REACT_APP_OKTA_APP_REDIRECT_URL,
});

const App = () => {
  const logger = useLogger();
  const settingsContext = useContext(SettingsContext);
  const location = useLocation();

  useEffect(() => {
    //only log on initial load
    logger.AI.info(
      `Starting SafeKey UI - Version: ${settingsContext.uiUpdateDateVersion}`
    );
  }, []);

  const lockOrderContext = useContext(LockOrderContext);
  const {
    setOrderID,
    setCcid,
    setSapLineNumber,
    setOrderPONumber,
    setLoadByLineNumber,
    setDesignID,
    setLoadByDesignID,
    setOrderCurrency,
    setIsQuote,
    setSalesArea,
    setQuoteNumberForTransfer,
    setSafekeyOrder,
  } = lockOrderContext;
  const {
    reviewPassCode,
    setReviewPassCode,
    setGuestAuthenticationKey,
    setSystemReadOnlyMode,
    setRequestURLParameters,
  } = useContext(AuthContext);

  const [isSummaryClicked, setIsSummaryClicked] = useState(false);

  const navigate = useNavigate();

  const restoreOriginalUri = (_oktaAuth: OktaAuth, originalUri: string) => {
    logger.UI.log('App - restoreOriginalUri', originalUri);
    navigate(toRelativeUrl(originalUri || '/', window.location.origin), {
      replace: true,
    });
  };

  const [loadAsGuest, setLoadAsGuest] = useState(false);
  const [loadAsUser, setLoadAsUser] = useState(false);
  const [loadDefault, setLoadDefault] = useState(false);

  useEffect(() => {
    //clear object change listener on navigation
    logger.UI.log('loacation key changed', location.key);
    lockOrderContext.setWarnBeforeNavigate(false);
  }, [location.key]);

  useEffect(() => {
    //url format
    //  new order :: designid=new
    //  https://<client-url>/?ccid=<CCID>&sapordernumber=<ORDERID>&saplinenumber=<LINEITEM>&sapquotenumber=<QUOTEID>&designid=<DESIGNID>&PO=<PO>&currency=<CURRENCY>

    // new quote :: designid=quote
    //              salesarea = 11001050 - concatenated
    //  https://<client-url>/?ccid=<CCID>&sapquotenumber=<QUOTEID>&linenumber=<LINEITEM>&designid=<DESIGNID>&salesarea=<IDENTIFIER>&PO=<PO>&currency=<CURRENCY>

    const queryString = window.location.search;
    let urlParams = new URLSearchParamsLowerCaseKey(queryString);
    let newCcid = urlParams.get('ccid');
    let newSapOrderNumber =
      urlParams.get('sapordernumber')?.toUpperCase() ??
      urlParams.get('orderid')?.toUpperCase();
    let newReviewPassCode = urlParams.get('review');
    let newLineNumber =
      urlParams.get('linenumber') ?? urlParams.get('saplinenumber');
    let newDesignID = urlParams.get('designid')?.toLowerCase();
    let currency = urlParams.get('currency');
    let newQuoteNumber =
      urlParams.get('sapquotenumber')?.toUpperCase() ??
      urlParams.get('quotenumber')?.toUpperCase();
    let newSalesArea = urlParams.get('salesarea');
    let newOrderID = urlParams.get('id');
    let newPONumber = urlParams.get('po') ?? urlParams.get('ponumber');

    if (newDesignID === '' && newSapOrderNumber) {
      newDesignID = 'new';
    } else if (newDesignID === '' && newQuoteNumber && !newSapOrderNumber) {
      newDesignID = 'quote';
    }

    if (
      !settingsContext.featureManagement.quoting &&
      (newDesignID === 'quote' || newQuoteNumber)
    ) {
      logger.UI.log('Quoting feature is disabled, navigate to customer search');
      toast.error(
        'Quoting feature is disabled. Please contact your administrator for assistance.'
      );
      setIsQuote(false);
      navigate('/customer-search');
      return;
    }

    if (
      newCcid ||
      newSapOrderNumber ||
      newDesignID ||
      newReviewPassCode ||
      newLineNumber ||
      newOrderID ||
      newPONumber ||
      currency ||
      newSalesArea ||
      newQuoteNumber
    ) {
      setRequestURLParameters(queryString);
      logger.UI.log('URL Parameters:', queryString);
      logger.AI.info(`Querystring: ${queryString}`);
    } else if (window.location.pathname === '/') {
      logger.UI.log('No URL Parameters, navigate to customer search');
      setSalesArea('');
      navigate('/customer-search');
      return;
    }

    if (newReviewPassCode && newOrderID) {
      //parameters for customer view
      logger.UI.log(
        `Set Context for Customer View from URL Parameters- reviewPassCode: ${newReviewPassCode}, orderID: ${newOrderID}`
      );
      if (newLineNumber) {
        setLoadByLineNumber(true);
        setSapLineNumber(newLineNumber.toString());
      } else {
        setLoadByLineNumber(false);
      }

      if (newDesignID) {
        setDesignID(newDesignID);
        setLoadByDesignID(!isNaN(parseInt(newDesignID)));
      } else {
        setDesignID('');
        setLoadByDesignID(false);
      }

      if (newReviewPassCode.toString() !== reviewPassCode) {
        //reset guest authentication key
        setGuestAuthenticationKey('');
      }

      setReviewPassCode(newReviewPassCode.toString());
      setOrderID(newOrderID.toString());

      if (currency) {
        setOrderCurrency(currency);
      }

      setSalesArea('');
      setLoadAsGuest(true);
      setSystemReadOnlyMode(true);
    } else if (
      (newSapOrderNumber || newQuoteNumber || newDesignID === 'quote') &&
      newCcid
    ) {
      //minimum parameters for loading a system as a user
      logger.UI.log(
        `Set Context for User View for new quote/order- sapOrderNumber: ${newSapOrderNumber}, quoteNumber: ${newQuoteNumber}, designID: ${newDesignID}, ccid: ${newCcid}`
      );

      if (newLineNumber) {
        setSapLineNumber(newLineNumber.toString());
        setLoadByLineNumber(true);
      } else {
        setLoadByLineNumber(false);
      }

      if (newDesignID) {
        setDesignID(newDesignID);
        setLoadByDesignID(!isNaN(parseInt(newDesignID)));
        if (newPONumber) {
          setOrderPONumber(newPONumber);
        } else if (newDesignID === 'quote' || newDesignID === 'new') {
          //if it is a new order or quote, clear the po number from any previous sessions
          setOrderPONumber('');
        }
      } else {
        setDesignID('');
        setLoadByDesignID(false);
        setOrderPONumber('');
      }

      if (newSalesArea) {
        setSalesArea(newSalesArea ?? '');
      } else {
        setSalesArea('');
      }

      setCcid(newCcid.toString().padStart(10, '0'));
      if (newSapOrderNumber && newDesignID !== 'quote') {
        if (
          newQuoteNumber &&
          newDesignID === 'new' &&
          newQuoteNumber !== newSapOrderNumber
        ) {
          setQuoteNumberForTransfer(newQuoteNumber);
        } else {
          setQuoteNumberForTransfer('');
        }

        setSafekeyOrder((prev: Order) => ({ ...prev, sapOrderNumber: newSapOrderNumber?.toString().padStart(10, '0') }));
        setIsQuote(false);
      } else if (newQuoteNumber) {
        setSafekeyOrder((prev: Order) => ({ ...prev, sapOrderNumber: newQuoteNumber?.toString() }));
        setIsQuote(true);
      } else if (newDesignID === 'quote') {
        setSafekeyOrder((prev: Order) => ({ ...prev, sapOrderNumber: '' }));
        setIsQuote(true);
      }

      if (currency) {
        setOrderCurrency(currency);
      }

      setLoadAsUser(true);
      setSystemReadOnlyMode(false);
    } else if (newCcid) {
      logger.UI.log(
        `Set Context for User View for new order- ccid: ${newCcid}`
      );
      setCcid(newCcid.toString().padStart(10, '0'));

      setSapLineNumber('');
      setLoadByLineNumber(false);
      setDesignID('');
      setLoadByDesignID(false);
      setSafekeyOrder((prev: Order) => ({ ...prev, sapOrderNumber: '' }));
      setOrderCurrency('');
      setLoadAsUser(true);
      setSystemReadOnlyMode(false);
      setSalesArea('');
      setIsQuote(false);
    } else if (newOrderID && newSapOrderNumber) {
      //for viewing from customer link
      logger.UI.log(
        `Set Context for Customer View from customer link- orderID: ${newOrderID}, sapOrderNumber: ${newSapOrderNumber}`
      );
      setOrderID(newSapOrderNumber.toString());
      setSapLineNumber('');
      setLoadByLineNumber(false);
      setDesignID('');
      setLoadByDesignID(false);
      setCcid('');
      setOrderCurrency('');
      setSalesArea('');
      setReviewPassCode(newOrderID.toString());
      setLoadAsGuest(true);
      setSystemReadOnlyMode(true);
    } else {
      logger.UI.log(
        'No Condition matched to handle URL Parameters, load as default. Includes: Okta Redirect'
      );
      setLoadDefault(true);
      //NOTE: Do not clear any required context here, it is needed for the next page load after Okta redirect
    }
  }, []);

  return (
    <Security
      oktaAuth={oktaAuth}
      restoreOriginalUri={restoreOriginalUri}
    >
      <IdentityContextProvider>
        <ErrorBoundary>
          <LookupContextProvider>
            <div className='App'>
              <Suspense fallback={<Spinner />}>
                <Routes>
                  <Route
                    path='/'
                    element={
                      <HomePage
                        loadAsGuest={loadAsGuest}
                        setLoadAsGuest={setLoadAsGuest}
                        loadAsUser={loadAsUser}
                        setLoadAsUser={setLoadAsUser}
                        loadDefault={loadDefault}
                        setLoadDefault={setLoadDefault}
                      />
                    }
                  />
                  <Route
                    path='/login/callback'
                    element={
                      <LoginCallback
                        errorComponent={() => {
                          //localStorage.clear()
                          try {
                            localStorage.removeItem('bearerToken');
                            localStorage.removeItem('okta-token-storage');
                          } catch (e) {}
                          navigate('/authorize');
                          return null;
                        }}
                      />
                    }
                  />
                  {/* <Route exact path="/system-selection" element={<KeySystemSelectionPage />} /> */}
                  <Route
                    path='/authorize'
                    element={<Authorize />}
                  />

                  <Route
                    path='/customer-search'
                    element={
                      <BoundedElement
                        path='/customer-search'
                        element={<CustomerSearchPage />}
                      />
                    }
                  />
                  <Route
                    path='/add-customer'
                    element={
                      <BoundedElement
                        path='/add-customer'
                        element={<AddCustomerPage />}
                      />
                    }
                  />
                  <Route
                    path='/system-add-or-edit'
                    element={
                      <BoundedElement
                        path='/system-add-or-edit'
                        element={<SystemPreProcessPage />}
                      />
                    }
                  />
                  <Route
                    path='/system-start'
                    element={
                      <BoundedElement
                        path='/system-start'
                        element={<SystemStartPage />}
                      />
                    }
                  />
                  <Route
                    path='/key-system'
                    element={
                      <BoundedElement
                        path='/key-system'
                        element={
                          <KeySystemPage
                            isSummaryClicked={isSummaryClicked}
                            setIsSummaryClicked={setIsSummaryClicked}
                          />
                        }
                      />
                    }
                  />
                  <Route
                    path='/set'
                    element={
                      <BoundedElement
                        path='/set'
                        element={
                          <SetPage
                            isSummaryClicked={isSummaryClicked}
                            setIsSummaryClicked={setIsSummaryClicked}
                          />
                        }
                      />
                    }
                  />
                  <Route
                    path='/charting-file'
                    element={
                      <BoundedElement
                        path='/charting-file'
                        element={<ChartingFileSummaryPage />}
                      />
                    }
                  />
                  <Route
                    path='/submit-system'
                    element={
                      <BoundedElement
                        path='/submit-system'
                        element={<SubmitReviewPage />}
                      />
                    }
                  />
                  <Route
                    path='/error'
                    element={
                      <BoundedElement
                        path='/error'
                        element={<ErrorPage />}
                      />
                    }
                  />
                  <Route
                    path='/edit-engraving'
                    element={
                      <BoundedElement
                        path='/edit-engraving'
                        element={<EditEngravingPage />}
                      />
                    }
                  />
                  <Route
                    path='/reorder-locks'
                    element={
                      <BoundedElement
                        path='/reorder-locks'
                        element={<ReorderLocksPage />}
                      />
                    }
                  />
                  <Route
                    path='/submit-review'
                    element={
                      <BoundedElement
                        path='/submit-review'
                        element={<SubmitReviewPage />}
                      />
                    }
                  />
                  <Route
                    path='/user-management'
                    element={
                      <BoundedElement
                        path='/user-management'
                        element={<UserManagementPage />}
                      />
                    }
                  />
                  <Route
                    path='/'
                    element={<ReorderLocksPage />}
                  />
                </Routes>
              </Suspense>
              {/* Display content in debug mode */}
              {settingsContext.settings.debugMode && (
                <label
                  style={{
                    position: 'fixed',
                    left: '.5em',
                    bottom: '.5em',
                    color: 'orange',
                  }}
                >
                  {JSON.stringify(lockOrderContext.systemState)} |{' '}
                  {lockOrderContext.warnBeforeNavigate?.toString()}
                </label>
              )}
              <HelpButton />
              <NavigationWarningAlert />
            </div>
          </LookupContextProvider>
        </ErrorBoundary>
      </IdentityContextProvider>
    </Security>
  );
};

const AppWrapper = () => {
  const defaultTheme = createTheme();
  let theme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
        xxl: 1800,
      },
    },
    typography: {
      bannerTitle: {
        fontFamily: 'Inter, sans-serif',
        fontSize: '1.5rem',
        fontWeight: 700,
        color: '#333',
        textShadow: '2px 2px 5px rgba(0, 0, 0, 0.2)',
        lineHeight: 1.2,
        [defaultTheme.breakpoints.up('sm')]: {
          fontSize: '2rem',
        },
        [defaultTheme.breakpoints.up('md')]: {
          fontSize: '2.5rem',
        },
        [defaultTheme.breakpoints.up('lg')]: {
          fontSize: '2.75rem',
        },
      },
      bannerSubtitle: {
        fontFamily: 'Inter, sans-serif',
        fontSize: '1.12rem',
        color: '#666',
        textShadow: '1px 1px 3px rgba(0, 0, 0, 0.2)',
        lineHeight: 1.2,
      },
      designTitle: {
        fontFamily: 'Inter, sans-serif',
        fontStyle: 'normal',
        fontWeight: 700,
        fontSize: '1rem',
        color: 'var(--brady-blue)',
        textShadow: '1px 1px 3px rgba(0, 0, 0, 0.2)',
        lineHeight: '1.2rem',
      },
      h1: {
        fontFamily: 'Inter, sans-serif',
        fontSize: '2.25rem',
        fontWeight: 700,
        color: '#333',
        lineHeight: 1.2,
      },
      h2: {
        fontFamily: 'Inter, sans-serif',
        fontSize: '1.5rem',
        fontWeight: 700,
        color: '#333',
        lineHeight: 1.2,
        // [defaultTheme.breakpoints.up('sm')]: {
        //   fontSize: '2rem',
        // },
        // [defaultTheme.breakpoints.up('md')]: {
        //   fontSize: '2.5rem',
        // },
        // [defaultTheme.breakpoints.up('lg')]: {
        //   fontSize: '2.75rem',
        // }
      },
      h3: {
        fontFamily: 'Inter, sans-serif',
        fontSize: '1.5rem',
        fontWeight: 700,
        color: '#333',
        lineHeight: 1.2,
      },
      h4: {
        fontFamily: 'Inter, sans-serif',
        fontSize: '1.17rem',
        fontWeight: 700,
        color: '#333',
        lineHeight: 1.2,
      },
      h5: {
        fontFamily: 'Inter, sans-serif',
        fontSize: '1rem',
        fontWeight: 700,
        color: '#333',
        lineHeight: 1.2,
      },
    },
    components: {
      // MuiContainer: {
      //   styleOverrides: {
      //     maxWidthXs: '',
      //     maxWidthSm: '100%',
      //     maxWidthMd: '100%',
      //     maxWidthLg: '100%',
      //     maxWidthXl: '100%',
      //     maxWidthXxl: '100%'
      //   }
      // },
      MuiStack: {
        defaultProps: {
          useFlexGap: true,
          spacing: 2,
        },
        styleOverrides: {
          root: {
            justifyContent: 'flex-start',
            alignItems: 'center',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          },
        },
      },
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            bannerTitle: 'h1',
            bannerSubtitle: 'p',
            designTitle: 'h2',
          },
        },
      },
    },
  });

  theme = responsiveFontSizes(theme);

  return (
    <BrowserRouter>
      <SettingsContextProvider>
        <LockOrderContextProvider>
          <AuthContextProvider>
            <ResponsiveContextProvider>
              <ToastContainer
                autoClose={3000}
                hideProgressBar={true}
              />
              <ThemeProvider theme={theme}>
                <SettingsContext.Consumer>
                  {({ featureManagement }) =>
                    featureManagement.isLoaded ? (
                      (<App />) // Require the feature gates to be loaded before rendering the app
                    ) : (
                      (<Spinner />) // Or any loading indicator
                    )
                  }
                </SettingsContext.Consumer>
              </ThemeProvider>
            </ResponsiveContextProvider>
          </AuthContextProvider>
        </LockOrderContextProvider>
      </SettingsContextProvider>
    </BrowserRouter>
  );
};

export default AppWrapper;
